// Unified API client. Same surface in demo and live mode so the UI is identical
// (spec 8.6, AC-X-003 "local demo mock-flagged").
//
// - Demo mode (default for `npm run dev`): uses the in-memory mockApi. Synthetic
//   data only, no real customer data, no real Bridge, no third-party model calls.
//   A visible "演示数据" badge marks demo mode so it can never be confused with
//   a live tenant.
// - Live mode (VITE_DEMO_MODE=false): talks to the real FastAPI backend at
//   /api/luo/v1/* with CSRF, 429/Retry-After handling, and structured errors.
//
// The raw invite token is NEVER placed in a URL query string or stored client-side
// (AC-B-025). It lives only in the URL fragment long enough to be exchanged at
// POST /session, then history.replaceState strips it (see routes/Invite.tsx).

import { mockApi } from './mock';
import type { CooperationInput, CooperationResponse, Feedback, SessionHandle } from './types';

export class ApiError extends Error {
  status: number;
  code: string;
  requestId: string;
  retryAfter?: number;
  constructor(status: number, code: string, message: string, requestId: string, retryAfter?: number) {
    super(message);
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.retryAfter = retryAfter;
  }
}

const DEMO = import.meta.env.VITE_DEMO_MODE !== 'false'; // default demo

export function isDemoMode(): boolean {
  return DEMO;
}

const API_BASE = '/api/luo/v1';

function uploadBlob(file: { name: string; type: string; blob?: Blob }): Blob {
  const type = file.type || (file.name.toLowerCase().endsWith('.log') ? 'text/plain' : 'application/octet-stream');
  if (file.blob?.type === type) return file.blob;
  return new Blob(file.blob ? [file.blob] : [], { type });
}

function csrfToken(): string | null {
  // Cookie-to-header double-submit pattern (AC-B-005). The backend sets a
  // non-session csrf cookie; we echo its value as a header on writes.
  const m = document.cookie.match(/(?:^|;\s*)luo_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

async function parseError(res: Response): Promise<ApiError> {
  let code = 'http_error';
  let message = `请求失败（${res.status}）`;
  let requestId = `http_${Date.now().toString(36)}`;
  try {
    const body = await res.json();
    if (body?.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      requestId = body.error.requestId ?? requestId;
    }
  } catch {
    // non-json body; keep defaults
  }
  const retryAfter = res.status === 429 ? Number(res.headers.get('Retry-After')) || undefined : undefined;
  return new ApiError(res.status, code, message, requestId, retryAfter);
}

const liveApi = {
  async createCooperation(input: CooperationInput): Promise<CooperationResponse> {
    const form = new FormData();
    form.append('name', input.name);
    form.append('contact', input.contact);
    form.append('company', input.company);
    form.append('project_type', input.projectType);
    form.append('summary', input.summary);
    form.append('detail', input.detail);
    form.append('budget', input.budget);
    form.append('timeframe', input.timeframe);
    form.append('consent', String(input.consent));
    form.append('website', input.website);
    for (const file of input.files) {
      form.append('attachments', uploadBlob(file), file.name);
    }
    const res = await fetch(`${API_BASE}/cooperation`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) throw await parseError(res);
    return res.json();
  },
  async bootstrapSession(token: string): Promise<SessionHandle> {
    const res = await fetch(`${API_BASE}/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ token }),
    });
    if (!res.ok) throw await parseError(res);
    return res.json();
  },
  async listFeedback(): Promise<Feedback[]> {
    const res = await fetch(`${API_BASE}/feedback`, { credentials: 'include' });
    if (!res.ok) throw await parseError(res);
    // F8: backend returns {data, nextCursor}; the approved List UI consumes a
    // single page, so unwrap `.data`. (Cursor-based load-more is a future
    // design-approved enhancement; the endpoint is already paginated.)
    const body = await res.json();
    return body.data as Feedback[];
  },
  async getFeedback(id: string): Promise<Feedback> {
    const res = await fetch(`${API_BASE}/feedback/${encodeURIComponent(id)}`, { credentials: 'include' });
    if (!res.ok) throw await parseError(res);
    return res.json();
  },
  async createFeedback(input: {
    summary: string;
    detail: string;
    files: { name: string; size: number; type: string; blob?: Blob }[];
  }): Promise<Feedback> {
    const form = new FormData();
    form.append('summary', input.summary);
    form.append('detail', input.detail);
    for (const f of input.files) {
      form.append('attachments', uploadBlob(f), f.name);
    }
    const headers: Record<string, string> = {};
    const csrf = csrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
    const res = await fetch(`${API_BASE}/feedback`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: form,
    });
    if (!res.ok) throw await parseError(res);
    return res.json();
  },
  async submitMoreInfo(id: string, reply: string): Promise<Feedback> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const csrf = csrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
    const res = await fetch(`${API_BASE}/feedback/${encodeURIComponent(id)}/more-info`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ reply }),
    });
    if (!res.ok) throw await parseError(res);
    return res.json();
  },
  async reopenFeedback(id: string): Promise<Feedback> {
    const headers: Record<string, string> = {};
    const csrf = csrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
    const res = await fetch(`${API_BASE}/feedback/${encodeURIComponent(id)}/reopen`, {
      method: 'POST',
      headers,
      credentials: 'include',
    });
    if (!res.ok) throw await parseError(res);
    return res.json();
  },
  async confirmResolved(id: string): Promise<Feedback> {
    const headers: Record<string, string> = {};
    const csrf = csrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
    const res = await fetch(`${API_BASE}/feedback/${encodeURIComponent(id)}/confirm-resolved`, {
      method: 'POST',
      headers,
      credentials: 'include',
    });
    if (!res.ok) throw await parseError(res);
    return res.json();
  },
};

// Demo mode wraps the mock so its thrown errors are ApiError instances too, and
// strips the blob field the UI passes (the mock only needs name/size/type).
async function demoWrap<T>(p: Promise<T>): Promise<T> {
  try {
    return await p;
  } catch (e) {
    const any = e as { status?: number; code?: string; message?: string; requestId?: string };
    throw new ApiError(
      any.status ?? 500,
      any.code ?? 'mock_error',
      any.message ?? '演示接口错误',
      any.requestId ?? `demo_${Math.random().toString(36).slice(2, 8)}`,
    );
  }
}

export const api = DEMO
  ? {
      createCooperation: (input: CooperationInput) => demoWrap(mockApi.createCooperation(input)),
      bootstrapSession: (t: string) => demoWrap(mockApi.bootstrapSession(t)),
      listFeedback: () => demoWrap(mockApi.listFeedback()),
      getFeedback: (id: string) => demoWrap(mockApi.getFeedback(id)),
      createFeedback: (input: {
        summary: string;
        detail: string;
        files: { name: string; size: number; type: string; blob?: Blob }[];
      }) =>
        demoWrap(
          mockApi.createFeedback({
            summary: input.summary,
            detail: input.detail,
            files: input.files.map((f) => ({ name: f.name, size: f.size, type: f.type })),
          }),
        ),
      submitMoreInfo: (id: string, reply: string) => demoWrap(mockApi.submitMoreInfo(id, reply)),
      reopenFeedback: (id: string) => demoWrap(mockApi.reopenFeedback(id)),
      confirmResolved: (id: string) => demoWrap(mockApi.confirmResolved(id)),
    }
  : liveApi;
